import type { FC } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, pluralize } from '@/lib/format'
import { useCart } from '@/components/order/cart-provider'
import { ButtonLink } from '@/components/generals/button'
import { Wordmark } from '@/components/generals/wordmark'
import { MobileMenu } from '@/components/generals/mobile-menu'

import { NAV_LINKS, SHELL } from '@/constants/site'

export const Header: FC = () => {
    const router = useRouter()
    const { count, hydrated } = useCart()
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    // Slides away while reading down the page and comes back as soon as you scroll up.
    useEffect(() => {
        let lastY = window.scrollY
        let frame = 0

        const update = (): void => {
            frame = 0

            const y = window.scrollY
            const delta = y - lastY

            setScrolled(y > 8)

            if (y < 120) {
                setHidden(false)
                lastY = y

                return
            }

            if (Math.abs(delta) < 8) return

            setHidden(delta > 0)
            lastY = y
        }

        const onScroll = (): void => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)

            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [])

    const isHidden = hidden && !menuOpen

    // Sticky bars further down the page (menu categories, order summary) sit right under the header.
    useEffect(() => {
        document.documentElement.style.setProperty('--header-offset', isHidden ? '0px' : 'var(--header-height)')
    }, [isHidden])

    useEffect(() => {
        const close = (): void => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)
    const showCount = hydrated && count > 0

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-40 h-16 border-b bg-background/95 backdrop-blur-sm transition-[translate,border-color] duration-500 ease-(--ease-soft) focus-within:translate-y-0',
                    isHidden ? '-translate-y-full' : 'translate-y-0',
                    scrolled ? 'border-line' : 'border-transparent'
                )}
            >
                <div className={cn(SHELL, 'grid h-full grid-cols-[1fr_auto] items-center gap-6 lg:grid-cols-[1fr_auto_1fr]')}>
                    <Link href='/' aria-label='La Douceur، صفحه‌ی اصلی' className='justify-self-start py-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber'>
                        <Wordmark />
                    </Link>

                    <nav aria-label='منوی اصلی' className='hidden lg:block'>
                        <ul className='flex items-center gap-9'>
                            {NAV_LINKS.map(link => {
                                const active = isActive(link.href)

                                return (
                                    <li key={link.href}>
                                        <Link
                                            href={link.href}
                                            aria-current={active ? 'page' : undefined}
                                            className='link-underline py-1 text-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber'
                                            style={active ? { backgroundSize: '100% 1px' } : undefined}
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center justify-end gap-1'>
                        <ButtonLink href='/order' size='sm' className='hidden lg:inline-flex'>
                            پیش‌سفارش بدهید

                            {showCount && (
                                <span className='-me-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground px-1.5 text-[0.6875rem] leading-none font-semibold text-primary tabular-nums'>
                                    {formatNumber(count)}
                                    <span className='sr-only'> عدد در سبد سفارش شما</span>
                                </span>
                            )}
                        </ButtonLink>

                        {showCount && (
                            <Link
                                href='/order'
                                className='relative inline-flex size-11 items-center justify-center focus-visible:outline-2 focus-visible:outline-amber lg:hidden'
                                aria-label={`سبد سفارش شما، ${pluralize(count, 'عدد')}`}
                            >
                                <ShoppingBag className='size-5' aria-hidden='true' />

                                <span className='absolute top-1.5 end-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.625rem] leading-none font-semibold text-primary-foreground tabular-nums'>
                                    {formatNumber(count)}
                                </span>
                            </Link>
                        )}

                        <button
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            className='-me-2 inline-flex h-11 cursor-pointer items-center gap-2 px-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-amber lg:hidden'
                        >
                            منو
                            <Menu className='size-5' aria-hidden='true' />
                        </button>
                    </div>
                </div>
            </header>

            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
        </>
    )
}
